/// <amd-module name="Environments/Landing/ActionsCreators/EnvironmentsActionsCreator" />
import { EnvironmentJobStatus, IEnvironmentObject, IEnvironmentsResult } from "Environments/Client/EnvironmentContracts";
import { MessagesActions } from "Environments/EnvironmentsCommon/MessagesActions";
import { Action, ActionCreatorBase } from "Environments/Flux/ActionCreatorBase";
import { ActionsHubManager } from "Environments/Flux/ActionsHubManager";
import { EnvironmentsActions } from "Environments/Landing/Actions/EnvironmentsActions";
import * as Constants from "Environments/Landing/Common/Constants";
import { IVssContributionService } from "VSS/Platform/Contribution";

import { CheckIssueData, ResourceCheckIssue } from "Checks/Client/Contracts/PipelinesChecks";
import { FeatureFlagConstants, ResourceTypeNames } from "Environments/Landing/Generated/Constants";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

const TableRowDisplayCount: number = 20;

const EnvironmentsView: string = "environments";
export class EnvironmentsActionsCreator extends ActionCreatorBase {

    public static getKey(): string {
        return "environments-action-creator";
    }

    public initialize(instanceId?: string) {
        super.initialize(instanceId);

        this._actions = ActionsHubManager.CreateActionsHub<EnvironmentsActions>(EnvironmentsActions, instanceId);
        this._messageActions = ActionsHubManager.CreateActionsHub<MessagesActions>(MessagesActions, instanceId);
    }

    public getEnvironmentWithFilters(pageContext: IVssPageContext, includeLastCompletedRequest: boolean, name?: string, environmentJobStatus: EnvironmentJobStatus = EnvironmentJobStatus.None, top: number = TableRowDisplayCount): void {
        this._getEnvironmentsInternal(pageContext, this._actions.setEnvironments, includeLastCompletedRequest, name, environmentJobStatus, undefined, top);
    }

    public fetchMoreEnvironments(pageContext: IVssPageContext, continuationToken: string, name?: string, environmentJobStatus: EnvironmentJobStatus = EnvironmentJobStatus.None, top: number = TableRowDisplayCount): void {
        this._getEnvironmentsInternal(pageContext, this._actions.appendEnvironments, true, name, environmentJobStatus, continuationToken, top);
    }

    private _getEnvironmentsInternal(
        pageContext: IVssPageContext,
        action: Action<IEnvironmentsResult>,
        includeLastCompletedRequest: boolean,
        name?: string,
        environmentJobStatus: EnvironmentJobStatus = EnvironmentJobStatus.None,
        continuationToken?: string,
        top: number = TableRowDisplayCount) {

        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        const promise = contributionService.getDataAsync<IEnvironmentsResult>(Constants.EnvironmentsDataProviderContribution, {includeLastCompletedRequest: includeLastCompletedRequest, name: name, continuationToken: continuationToken, top: top, environmentJobStatus: environmentJobStatus})!;
        promise.then((environmentsResult: IEnvironmentsResult) => {
            const _showCheckIssues = isFeatureFlagEnabled(
                pageContext,
                FeatureFlagConstants.EnableChecksScalabilityResourceIssues,
                false
            );
            if (_showCheckIssues && environmentsResult.environments?.length > 0){
                let resourceParams: { [key: string]: string } = {
                    "resourceTypeName" : ResourceTypeNames.Environment,
                    "resourceIds" : environmentsResult.environments.map(r => r.id).join(",")
                };

                contributionService
                    .getDataAsync<CheckIssueData>(Constants.CheckIssuesDataProviderContribution, resourceParams)
                    .then((CheckIssueDataResult: CheckIssueData) => {
                        let environmentsWithIssuesResult: ResourceCheckIssue[] = CheckIssueDataResult?.resourcesWithCheckIssuesList;
                        if (environmentsWithIssuesResult?.length > 0){
                            environmentsResult.environments.map((environment: IEnvironmentObject) => {
                                environment.issue = environmentsWithIssuesResult
                                    .find((environmentWithIssues: ResourceCheckIssue) => environmentWithIssues.resource.id === environment.id.toString())
                                    ?.checkIssue;
                            });
                        }
                        action.invoke(environmentsResult);
                    });
            }else{
                action.invoke(environmentsResult);
            }
        },
        (error) => {
            this._messageActions.addMessage.invoke({ key: EnvironmentsView, value: error });
        });
    }

    protected disposeInternal(): void {
        ActionsHubManager.DeleteActionsHub<EnvironmentsActions>(EnvironmentsActions, this.getInstanceId());
        ActionsHubManager.DeleteActionsHub<MessagesActions>(MessagesActions, this.getInstanceId());
    }

    private _actions: EnvironmentsActions;
    private _messageActions: MessagesActions;
}
